import { fxToAud } from '../config/fx';
import type {
  AustralianState,
  Citizenship,
  CurrencyCode,
  EmploymentType,
  ExistingProperty,
  IncomeStream,
  OtherLoan,
  PropertyType,
  PropertyUse,
  PurchaseAssessmentInput,
  Situation,
  TaxResidency,
} from '../types';

const TAX_RESIDENCY_VALUES: TaxResidency[] = [
  'australian_tax_resident',
  'non_tax_resident',
  'part_year_tax_resident_leaving_aus',
  'part_year_tax_non_resident_returning_aus',
  'unsure',
];

function normaliseTaxResidency(raw: unknown): TaxResidency {
  return typeof raw === 'string' &&
    TAX_RESIDENCY_VALUES.includes(raw as TaxResidency)
    ? (raw as TaxResidency)
    : 'unsure';
}

const FREQUENCY_TO_ANNUAL: Record<string, number> = {
  weekly: 52,
  fortnightly: 26,
  monthly: 12,
  quarterly: 4,
  yearly: 1,
  annually: 1,
};

const AU_STATE_CODES: AustralianState[] = [
  'NSW',
  'VIC',
  'QLD',
  'WA',
  'SA',
  'TAS',
  'NT',
  'ACT',
];

// Backend / form use lowercase codes (`nsw`, `vic`, `tas`, `nt`, `act`…).
// All 8 AU states/territories are accepted. TAS and NT use provisional
// stamp-duty brackets — a runtime warning surfaces this to the user.
function normaliseState(raw: unknown): AustralianState {
  if (typeof raw !== 'string') return 'NSW';
  const up = raw.toUpperCase();
  return (
    AU_STATE_CODES.includes(up as AustralianState) ? up : 'NSW'
  ) as AustralianState;
}

// Backend PropertyType enum has 7 values; the calc engine only models 3.
// Apartments/off-plan apartments → 'unit'; everything else → 'house'.
function normalisePropertyType(raw: unknown): PropertyType {
  if (raw === 'apartment' || raw === 'off_plan_apartment') return 'unit';
  if (raw === 'townhouse') return 'townhouse';
  return 'house';
}

// Backend MaritalStatusChoice: single | married | defacto | divorced | widowed.
function deriveSituation(
  maritalStatus: unknown,
  dependents: unknown,
): Situation {
  const coupled = maritalStatus === 'married' || maritalStatus === 'defacto';
  const hasDeps = num(dependents) > 0;
  if (coupled && hasDeps) return 'couple_1';
  if (coupled) return 'couple_0';
  if (hasDeps) return 'single_1';
  return 'single_0';
}

// Backend CitizenshipChoice: 5 values. Engine treats TR-visa + NZ + foreign
// as foreign-national tier (most restrictive) until tier rules are added.
function normaliseCitizenship(raw: unknown): Citizenship {
  if (raw === 'australian_citizen') return 'australian_citizen';
  if (raw === 'australian_pr_visa') return 'australian_pr_visa';
  return 'foreign_national';
}

// Backend IncomeSourceChoice → engine EmploymentType.
function deriveEmployment(incomeSource: unknown): EmploymentType {
  if (incomeSource === 'self_employed_company') return 'director';
  if (incomeSource === 'self_employed_trader_or_contractor')
    return 'selfemployed';
  return 'payg';
}

// Backend PropertyPurpose: owner_occupied | investment | initially_inv |
// holiday_home. Engine bucket is binary investment / owner_occupied.
function normalisePropertyUse(raw: unknown): PropertyUse {
  if (raw === 'investment' || raw === 'initially_inv') return 'investment';
  return 'owner_occupied';
}

function num(v: unknown, fallback = 0): number {
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  if (typeof v === 'string') {
    const n = parseFloat(v);
    return Number.isNaN(n) ? fallback : n;
  }
  return fallback;
}

function str<T extends string>(v: unknown, fallback: T): T {
  return typeof v === 'string' && v.length > 0 ? (v as T) : fallback;
}

function bool(v: unknown): boolean {
  return v === true || v === 'true' || v === 1 || v === '1';
}

function annualise(amount: number, frequency: unknown): number {
  const key = typeof frequency === 'string' ? frequency.toLowerCase() : '';
  const factor = FREQUENCY_TO_ANNUAL[key] ?? 1;
  return amount * factor;
}

// 2-year average for variable income (bonus, stock). When only one year has
// a value, that single value is used as-is (not halved) — matches typical
// lender practice of "if only one year of history, use it conservatively".
function avgYears(thisYear: number, lastYear: number): number {
  if (thisYear > 0 && lastYear > 0) return (thisYear + lastYear) / 2;
  return thisYear || lastYear;
}

// All monetary fields on IncomeStream are returned **in AUD** — the FX
// conversion happens here so the pipeline doesn't have to know about
// currencies. The `currency` field is kept on the stream only so per-lender
// shading lookups (some lenders shade non-AUD income) still have the
// original code.
export function buildIncomeStream(
  src: Record<string, unknown> | null | undefined,
  fx: (currency: CurrencyCode | string) => number,
  prefix: '' | 'coapplicant_' = '',
): IncomeStream {
  const get = (key: string) => src?.[`${prefix}${key}`];

  const incomeSource = String(get('income_source') ?? '');
  const isSelfEmployed =
    incomeSource === 'self_employed_company' ||
    incomeSource === 'self_employed_trader_or_contractor';

  // Self-employed use `company_salary_currency` (their primary income
  // currency); PAYG employees use `salary_currency`.
  const currency = str<CurrencyCode>(
    isSelfEmployed
      ? (get('company_salary_currency') ?? get('salary_currency'))
      : (get('salary_currency') ?? get('company_salary_currency')),
    'AUD',
  );
  const rate = fx(currency);

  // PAYG: declared base salary, annualised by salary frequency.
  // Self-employed: 2-year average of personal net income after taxes — this
  // is the canonical lender-assessable income for sole traders + directors.
  // (Without this, base_salary stays $0 for every self-employed applicant
  // and every lender fails on serviceability.)
  const baseLocal = isSelfEmployed
    ? avgYears(
        num(get('this_year_personal_net_income_after_taxes')),
        num(get('last_year_personal_net_income_after_taxes')),
      )
    : annualise(num(get('base_salary')), get('salary_frequency'));

  // Variable income items — 2-year average where both years filled.
  const bonusLocal = avgYears(
    num(get('this_year_annual_cash_bonus')),
    num(get('last_year_annual_cash_bonus')),
  );
  const stockLocal = avgYears(
    num(get('this_year_stock_options')),
    num(get('last_year_stock_options')),
  );

  const allowanceLocal = annualise(
    num(get('allowance_income')),
    get('allowance_frequency'),
  );

  const commissionLocal = annualise(
    num(get('commission_income')),
    get('commission_frequency'),
  );

  return {
    baseSalary: baseLocal * rate,
    bonus: bonusLocal * rate,
    allowance: allowanceLocal * rate,
    commission: commissionLocal * rate,
    stock: stockLocal * rate,
    currency,
  };
}

// Balances and repayment sums on the returned OtherLoan list are in AUD.
// `currency` is retained for display / future per-lender logic.
function buildOtherLoans(
  src: Record<string, unknown> | null | undefined,
  fx: (currency: CurrencyCode | string) => number,
  prefix: '' | 'coapplicant_' = '',
): OtherLoan[] {
  if (!src) return [];
  const countKey = `${prefix}non_property_loan`;
  const count = num(src[countKey]);
  const out: OtherLoan[] = [];
  for (let i = 1; i <= count; i++) {
    const currency = str<CurrencyCode>(
      src[`${prefix}non_property_loan_currency${i}`],
      'AUD',
    );
    const rate = fx(currency);
    out.push({
      loanType: str(src[`${prefix}non_property_loan_type${i}`], ''),
      currency,
      balance: num(src[`${prefix}non_property_loan_balance${i}`]) * rate,
      repaymentTerm: num(src[`${prefix}non_property_loan_repayment_term${i}`]),
      repaymentSum:
        num(src[`${prefix}non_property_loan_repayment_sum${i}`]) * rate,
      repaymentFrequency: str(
        src[`${prefix}non_property_loan_repayment_frequency${i}`],
        'monthly',
      ),
    });
  }
  return out;
}

// HECS / HELP debt is captured by the form as a non-property loan of type
// `student_loan`. The engine treats HECS as a binary trigger (flat $400/mo
// regardless of balance), so we only need to know whether ANY student-loan
// entry has a non-zero balance. Sum balances so multiple student-loan rows
// still register, and so the value reflects actual debt size if rules ever
// stop being binary.
function extractHecsBalance(loans: OtherLoan[]): number {
  return loans
    .filter((l) => l.loanType === 'student_loan')
    .reduce((sum, l) => sum + l.balance, 0);
}

// Student loans are routed to HECS handling — strip them from the generic
// other-loans list so their declared repayments aren't double-counted on top
// of the flat HECS monthly assessment.
function stripStudentLoans(loans: OtherLoan[]): OtherLoan[] {
  return loans.filter((l) => l.loanType !== 'student_loan');
}

// Existing properties = already-owned AU properties from properties[].
// `property_location: "australian"` is the AU/overseas flag (separate from
// primary_detail.property_location, which is the state code).
function buildExistingProperties(properties: unknown): ExistingProperty[] {
  if (!Array.isArray(properties)) return [];
  return properties
    .filter(
      (p: any) =>
        (p?.property_location === 'australian' ||
          p?.property_location === 'australia') &&
        p?.property_details?.property_owner === 'own',
    )
    .map((p: any) => {
      const d = p.property_details ?? {};
      return {
        value: num(d.property_value),
        monthlyRent:
          annualise(num(d.property_rent), d.property_rent_frequency) / 12,
        mortgageBalance: num(d.mortgage_loan_limit),
        state: normaliseState(d.state ?? d.property_state),
      };
    });
}

// Rent expense as a proxy for declared living when the user is renting.
// HEM still applies as the floor inside the calc engine.
function deriveDeclaredLiving(primary: any, income: any): number {
  if (income?.declared_monthly_living) {
    return num(income.declared_monthly_living);
  }
  if (primary?.current_residental_status === 'renting') {
    return annualise(num(primary.rent_expense), primary.frequency_of_rent) / 12;
  }
  return 0;
}

export function mapSummaryToAssessmentInput(
  summary: any,
  liveFxRates: Record<string, number>,
): PurchaseAssessmentInput | null {
  const data = summary?.data;
  if (!data) return null;

  // One closure used everywhere FX matters. `fxToAud` throws
  // `FxRateMissingError` if any required currency isn't in the live feed
  // — there is no static fallback. The caller catches and renders an
  // error state.
  const fx = (currency: CurrencyCode | string) =>
    fxToAud(currency, liveFxRates);

  const user = data.user_data ?? {};
  const primary = data.primary_detail ?? {};
  const income = data.income_detail ?? {};
  const properties = data.properties ?? [];

  const purchasePrice = num(primary.max_purchase_amount);
  const depositAvailable = num(primary.max_deposit_downpayment);
  const propertyState = normaliseState(primary.property_location);
  const propertyType = normalisePropertyType(primary.property_type);
  const propertyUse = normalisePropertyUse(primary.property_purpose);

  // `purchasing_under` is solo / joint — drives coapplicant existence.
  const isJoint =
    primary.purchasing_under === 'joint_borrower' ||
    bool(income.coapplicant_exists);

  // Credit-card limit is stored in the user's CC currency — convert to AUD.
  const ccCurrency = str<CurrencyCode>(income.credit_card_currency, 'AUD');
  const creditCardLimitAud = num(income.credit_card_limit) * fx(ccCurrency);

  const coappCcCurrency = str<CurrencyCode>(
    income.coapplicant_credit_card_currency,
    'AUD',
  );
  const coappCcLimitAud =
    num(income.coapplicant_credit_card_limit) * fx(coappCcCurrency);

  return {
    applicant: {
      firstName: str(user.first_name, ''),
      lastName: str(user.last_name, ''),
      email: str(user.email, ''),
      phone: str(user.phone, ''),
      citizenship: normaliseCitizenship(primary.citizenship),
      country: str(primary.country, 'AU'),
      employment: deriveEmployment(income.income_source),
      familySituation: deriveSituation(
        primary.marital_status,
        primary.number_of_dependents,
      ),
      taxResidency: normaliseTaxResidency(primary.tax_residency_status),
    },
    coapplicantExists: isJoint,
    primaryIncome: buildIncomeStream(income, fx),
    coapplicantIncome: isJoint
      ? buildIncomeStream(income, fx, 'coapplicant_')
      : null,
    liabilities: {
      creditCardLimit: creditCardLimitAud,
      creditCardCurrency: ccCurrency,
      hecsBalance: extractHecsBalance(buildOtherLoans(income, fx)),
      otherLoans: stripStudentLoans(buildOtherLoans(income, fx)),
    },
    coapplicantLiabilities: isJoint
      ? {
          creditCardLimit: coappCcLimitAud,
          creditCardCurrency: coappCcCurrency,
          otherLoans: stripStudentLoans(
            buildOtherLoans(income, fx, 'coapplicant_'),
          ),
        }
      : null,
    purchase:
      purchasePrice > 0
        ? {
            purchasePrice,
            depositAvailable,
            propertyState,
            propertyUse,
            propertyType,
            rentalYieldPct: num(primary.rental_yield_pct, 3.5),
          }
        : null,
    existingProperties: buildExistingProperties(properties),
    declaredMonthlyLiving: deriveDeclaredLiving(primary, income),
  };
}
